'use client';

import { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    hbspt?: {
      forms?: {
        create?: (config: {
          region?: string;
          portalId: string;
          formId: string;
          target: string;
        }) => void;
      };
    };
  }
}

interface HubspotFormProps {
  portalId: string;
  formId: string;
  region?: 'eu1' | 'na1';
}

const SDK_URL = 'https://js.hsforms.net/forms/embed/v2.js';

// Singleton: SDK loading promise shared across all form instances
let sdkLoadingPromise: Promise<void> | null = null;

/**
 * Load HubSpot SDK once, return promise that resolves when ready
 */
function loadSdk(): Promise<void> {
  if (window.hbspt?.forms?.create) {
    return Promise.resolve();
  }

  if (sdkLoadingPromise) {
    return sdkLoadingPromise;
  }

  sdkLoadingPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SDK_URL;
    script.async = true;
    script.onload = () => { resolve(); };
    script.onerror = () => {
      sdkLoadingPromise = null;
      reject(new Error('Failed to load HubSpot SDK'));
    };
    document.head.appendChild(script);
  });

  return sdkLoadingPromise;
}

/**
 * HubSpot Form Component
 *
 * - Intersection Observer: form only loads when visible
 * - Promise-based SDK loading: no polling
 */
export function HubspotForm({
  portalId,
  formId,
  region = 'eu1',
}: HubspotFormProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let isMounted = true;

    const initForm = () => {
      setStatus('loading');

      loadSdk()
        .then(() => {
          if (!isMounted) return;

          window.hbspt?.forms?.create?.({
            region,
            portalId,
            formId,
            target: `#hs-form-${formId}`,
          });
          setStatus('ready');
        })
        .catch((err: unknown) => {
          if (!isMounted) return;
          console.error('HubSpot SDK error:', err);
          setStatus('error');
        });
    };

    // If URL has a hash, load immediately (user might be navigating to an anchor)
    if (window.location.hash) {
      initForm();
      return () => {
        isMounted = false;
      };
    }

    // Otherwise use Intersection Observer for lazy loading
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        initForm();
      },
      { rootMargin: '600px' }
    );

    observer.observe(el);

    return () => {
      isMounted = false;
      observer.disconnect();
    };
  }, [portalId, formId, region]);

  return (
    <div
      ref={containerRef}
      id={`hs-form-${formId}`}
      className="-mt-10"
    >
      {status === 'error' && (
        <p className="text-sm text-red-600">
          Formular konnte nicht geladen werden.
        </p>
      )}
    </div>
  );
}
